import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useNavigation } from '../shared/hooks/useNavigation'
import { ROUTES } from '../shared/navigation'
import { 
  ArrowLeft, 
  Save, 
  Mail, 
  Phone, 
  User, 
  Calendar,
  Loader2,
  Lock,
  Tag,
  Plus,
  CreditCard,
  MapPin
} from '../components/icons'
import { clientService } from '../services/clientService'
import { useAuthStore } from '../stores/authStore'
import LabelChip from '../components/labels/LabelChip'
import LabelSelector from '../components/labels/LabelSelector'
import labelService from '../services/labelService'
import { extractLabelIds } from '../utils/labelUtils'
import { 
  handleCpfInput, 
  validateCpfChecksum, 
  handleCepInput, 
  lookupCep, 
  validateBrazilianFields, 
  cleanFormData, 
  BRAZILIAN_STATES 
} from '../utils/brazilianUtils'

const ClientFormPage = () => {
  const { clientId } = useParams() // If editing existing client
  const { navigate } = useNavigation()
  const { hasRole } = useAuthStore()
  
  const isEditing = Boolean(clientId && clientId !== 'new')
  const canManageClients = hasRole(['GESTOR', 'ATENDENTE'])

  // Form state - ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL LOGIC
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    password: '',
    date_of_birth: '',
    gender: '',
    cpf: '',
    address_street: '',
    address_number: '',
    address_complement: '',
    address_neighborhood: '',
    address_city: '',
    address_state: '',
    address_cep: '',
    is_active: true
  })

  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [validationErrors, setValidationErrors] = useState({})
  const [isLabelSelectorOpen, setIsLabelSelectorOpen] = useState(false)
  const [selectedLabels, setSelectedLabels] = useState([])
  const [isLookingUpCep, setIsLookingUpCep] = useState(false)

  // Handle permission check in useEffect instead of early return
  useEffect(() => {
    if (!canManageClients) {
      navigate(ROUTES.PROFESSIONAL.DASHBOARD)
    }
  }, [canManageClients, navigate])

  // Load client data if editing
  useEffect(() => {
    if (isEditing && clientId && clientId !== 'new') {
      loadClientData()
    }
  }, [clientId, isEditing])

  const loadClientData = async () => {
    try {
      setIsLoading(true)
      const client = await clientService.getClientById(clientId)
      
      if (client) {
        setFormData({
          full_name: client.full_name || '',
          email: client.email || '',
          phone_number: client.phone_number || '',
          password: '', // Don't populate password when editing
          date_of_birth: client.date_of_birth || '',
          gender: client.gender || '',
          cpf: client.cpf || '',
          address_street: client.address_street || '',
          address_number: client.address_number || '',
          address_complement: client.address_complement || '',
          address_neighborhood: client.address_neighborhood || '',
          address_city: client.address_city || '',
          address_state: client.address_state || '',
          address_cep: client.address_cep || '',
          is_active: client.is_active !== false
        })
        setSelectedLabels(client.labels || [])
      } else {
        setError('Cliente não encontrado')
      }
    } catch (error) {
      setError('Erro ao carregar dados do cliente')
    } finally {
      setIsLoading(false)
    }
  }

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  // Handle CPF input with formatting
  const handleCpfChange = (value) => {
    const formatted = handleCpfInput(value)
    handleChange('cpf', formatted)
  }

  // Handle CEP input with formatting and lookup
  const handleCepChange = async (value) => {
    const formatted = handleCepInput(value)
    handleChange('address_cep', formatted)
    
    // Auto-lookup address if CEP is complete
    if (formatted.length === 9) { // Format: 12345-678
      setIsLookingUpCep(true)
      try {
        const addressData = await lookupCep(formatted)
        if (addressData) {
          setFormData(prev => ({
            ...prev,
            address_cep: formatted,
            address_street: addressData.address_street || prev.address_street,
            address_neighborhood: addressData.address_neighborhood || prev.address_neighborhood,
            address_city: addressData.address_city || prev.address_city,
            address_state: addressData.address_state || prev.address_state
          }))
        }
      } catch (error) {
        // Ignore CEP lookup errors - user can enter address manually
      } finally {
        setIsLookingUpCep(false)
      }
    }
  }

  // Handle label management
  const handleLabelRemove = (labelToRemove) => {
    setSelectedLabels(prev => prev.filter(label => label.id !== labelToRemove.id))
  }

  const handleLabelSelectionChange = (newLabels) => {
    setSelectedLabels(newLabels)
  }

  // Validate form
  const validateForm = () => {
    const errors = {}

    if (!formData.full_name.trim()) {
      errors.full_name = 'Nome é obrigatório'
    }

    if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email inválido'
    }

    if (!isEditing && !formData.password.trim()) {
      errors.password = 'Senha é obrigatória'
    } else if (!isEditing && formData.password.length < 6) {
      errors.password = 'Senha deve ter pelo menos 6 caracteres'
    }

    if (formData.phone_number && !/^\d+$/.test(formData.phone_number.replace(/\D/g, ''))) {
      errors.phone_number = 'Telefone inválido'
    }

    // Validate Brazilian fields (CPF, CEP, address)
    const brazilianErrors = validateBrazilianFields(formData)
    Object.assign(errors, brazilianErrors)

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setIsSaving(true)
      setError('')

      // Clean and format data for submission
      const cleanedData = cleanFormData({
        ...formData,
        phone_number: formData.phone_number.replace(/\D/g, ''),
        email: formData.email.trim() || null
      })

      // For editing, remove password if empty (don't change password)
      if (isEditing && !cleanedData.password.trim()) {
        delete cleanedData.password
      }

      let result
      if (isEditing) {
        result = await clientService.updateClient(clientId, cleanedData)
      } else {
        result = await clientService.createClient(cleanedData)
      }

      if (result && result.id) {
        // Update labels for both create and edit operations
        try {
          const labelIds = extractLabelIds(selectedLabels);
          
          // Always update labels (even if empty array) to ensure consistency
          await labelService.updateUserLabels(result.id, labelIds);
        } catch (error) {
          // Continue even if labels fail - client was created/updated successfully
        }
        
        navigate(ROUTES.PROFESSIONAL.CLIENTS, {
          state: { 
            message: isEditing ? 'Cliente atualizado com sucesso' : 'Cliente criado com sucesso'
          }
        })
      } else {
        setError(isEditing ? 'Erro ao atualizar cliente' : 'Erro ao criar cliente')
      }
    } catch (error) {
      setError(isEditing ? 'Erro ao atualizar cliente' : 'Erro ao criar cliente')
    } finally {
      setIsSaving(false)
    }
  }

  // Format phone for display
  const formatPhoneInput = (value) => {
    const cleaned = value.replace(/\D/g, '')
    if (cleaned.length <= 11) {
      if (cleaned.length <= 2) return cleaned
      if (cleaned.length <= 7) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`
    }
    return value
  }

  // Loading state - Luxury Design
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-brand-light dark:bg-brand-dark">
        <div className="w-8 h-8 border-2 border-brand-dark dark:border-brand-light border-t-transparent animate-spin"></div>
      </div>
    )
  }

  // Permission check - render nothing if no permission
  if (!canManageClients) {
    return null
  }

  return (
    <div className="h-full flex flex-col bg-brand-light dark:bg-brand-dark">
      {/* Header - Luxury Design */}
      <div className="bg-brand-light dark:bg-brand-dark border-b border-brand-dark/10 dark:border-brand-light/10 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate(ROUTES.PROFESSIONAL.CLIENTS)}
              className="mr-3 p-2 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 border border-brand-dark/20 dark:border-brand-light/20 transition-all duration-300"
            >
              <ArrowLeft size={20} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
            </button>
            <div>
              <h1 className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">
                {isEditing ? 'Editar Cliente' : 'Novo Cliente'}
              </h1>
              <p className="text-brand-dark/60 dark:text-brand-light/60 text-sm tracking-wide">
                {isEditing ? 'Atualize as informações do cliente' : 'Preencha os dados do novo cliente'}
              </p>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className="bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light px-4 py-2 font-medium uppercase tracking-widest text-sm disabled:opacity-50 flex items-center hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
          >
            {isSaving ? (
              <div className="w-4 h-4 border-2 border-brand-light dark:border-brand-dark border-t-transparent animate-spin mr-2"></div>
            ) : (
              <Save size={16} className="mr-2" strokeWidth={1.5} />
            )}
            {isSaving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto mobile-scroll">
        {error && (
          <div className="mx-4 mt-4 p-4 bg-transparent border border-red-600/30 dark:border-red-400/30">
            <p className="text-red-600 dark:text-red-400 text-sm text-center tracking-wide">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="off" className="p-4 space-y-6">
          {/* Basic Information - Luxury Design */}
          <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 p-6">
            <h2 className="text-lg font-serif font-bold text-brand-dark dark:text-brand-light mb-4 tracking-wide">Informações Básicas</h2>
            
            <div className="space-y-4">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-brand-dark dark:text-brand-light mb-2 uppercase tracking-wider">
                  Nome Completo *
                </label>
                <div className="relative">
                  <User size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    placeholder="Nome completo do cliente"
                    className={`w-full pl-10 pr-4 py-3 bg-transparent border text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 tracking-wide ${
                      validationErrors.full_name
                        ? 'border-red-600/30 dark:border-red-400/30 focus:ring-red-600 dark:focus:ring-red-400'
                        : 'border-brand-dark/20 dark:border-brand-light/20 focus:ring-brand-dark dark:focus:ring-brand-light'
                    }`}
                  />
                </div>
                {validationErrors.full_name && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1 tracking-wide">{validationErrors.full_name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-brand-dark dark:text-brand-light mb-2 uppercase tracking-wider">
                  Email
                </label>
                <div className="relative">
                  <Mail size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="email@exemplo.com (opcional)"
                    autoComplete="off"
                    className={`w-full pl-10 pr-4 py-3 bg-transparent border text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 tracking-wide ${
                      validationErrors.email
                        ? 'border-red-600/30 dark:border-red-400/30 focus:ring-red-600 dark:focus:ring-red-400'
                        : 'border-brand-dark/20 dark:border-brand-light/20 focus:ring-brand-dark dark:focus:ring-brand-light'
                    }`}
                  />
                </div>
                {validationErrors.email && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1 tracking-wide">{validationErrors.email}</p>
                )}
              </div>

              {/* Password - Only show when creating new client */}
              {!isEditing && (
                <div>
                  <label className="block text-sm font-medium text-brand-dark dark:text-brand-light mb-2 uppercase tracking-wider">
                    Senha *
                  </label>
                  <div className="relative">
                    <Lock size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      autoComplete="new-password"
                      className={`w-full pl-10 pr-4 py-3 bg-transparent border text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 tracking-wide ${
                        validationErrors.password
                          ? 'border-red-600/30 dark:border-red-400/30 focus:ring-red-600 dark:focus:ring-red-400'
                          : 'border-brand-dark/20 dark:border-brand-light/20 focus:ring-brand-dark dark:focus:ring-brand-light'
                      }`}
                    />
                  </div>
                  {validationErrors.password && (
                    <p className="text-red-600 dark:text-red-400 text-sm mt-1 tracking-wide">{validationErrors.password}</p>
                  )}
                </div>
              )}

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-brand-dark dark:text-brand-light mb-2 uppercase tracking-wider">
                  Telefone
                </label>
                <div className="relative">
                  <Phone size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                  <input
                    type="tel"
                    value={formatPhoneInput(formData.phone_number)}
                    onChange={(e) => handleChange('phone_number', e.target.value)}
                    placeholder="(11) 99999-9999"
                    className={`w-full pl-10 pr-4 py-3 bg-transparent border text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 tracking-wide ${
                      validationErrors.phone_number
                        ? 'border-red-600/30 dark:border-red-400/30 focus:ring-red-600 dark:focus:ring-red-400'
                        : 'border-brand-dark/20 dark:border-brand-light/20 focus:ring-brand-dark dark:focus:ring-brand-light'
                    }`}
                  />
                </div>
                {validationErrors.phone_number && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1 tracking-wide">{validationErrors.phone_number}</p>
                )}
              </div>

              {/* Date of Birth */}
              <div>
                <label className="block text-sm font-medium text-brand-dark dark:text-brand-light mb-2 uppercase tracking-wider">
                  Data de Nascimento
                </label>
                <div className="relative">
                  <Calendar size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                  <input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => handleChange('date_of_birth', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 text-brand-dark dark:text-brand-light focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                  />
                </div>
              </div>

              {/* CPF */}
              <div>
                <label className="block text-sm font-medium text-brand-dark dark:text-brand-light mb-2 uppercase tracking-wider">
                  CPF
                </label>
                <div className="relative">
                  <CreditCard size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                  <input
                    type="text"
                    value={formData.cpf}
                    onChange={(e) => handleCpfChange(e.target.value)}
                    placeholder="000.000.000-00"
                    maxLength={14}
                    className={`w-full pl-10 pr-4 py-3 bg-transparent border text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 tracking-wide ${
                      validationErrors.cpf
                        ? 'border-red-600/30 dark:border-red-400/30 focus:ring-red-600 dark:focus:ring-red-400'
                        : 'border-brand-dark/20 dark:border-brand-light/20 focus:ring-brand-dark dark:focus:ring-brand-light'
                    }`}
                  />
                </div>
                {validationErrors.cpf && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1 tracking-wide">{validationErrors.cpf}</p>
                )}
              </div>
            </div>
          </div>

          {/* Additional Information - Luxury Design */}
          <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 p-6">
            <h2 className="text-lg font-serif font-bold text-brand-dark dark:text-brand-light mb-4 tracking-wide">Informações Adicionais</h2>

            <div className="space-y-4">
              {/* Gender */}
              <div>
                <label className="block text-sm font-medium text-brand-dark dark:text-brand-light mb-2 uppercase tracking-wider">
                  Gênero
                </label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleChange('gender', e.target.value)}
                  className="w-full py-3 px-4 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 text-brand-dark dark:text-brand-light focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                >
                  <option value="">Selecione o gênero</option>
                  <option value="MASCULINO">Masculino</option>
                  <option value="FEMININO">Feminino</option>
                  <option value="OUTROS">Outros</option>
                </select>
              </div>


              {/* Active Status */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                    className="w-4 h-4 bg-transparent border-brand-dark/20 dark:border-brand-light/20 focus:ring-brand-dark dark:focus:ring-brand-light"
                  />
                  <span className="ml-2 text-sm text-brand-dark dark:text-brand-light tracking-wide">Cliente ativo</span>
                </label>
              </div>
            </div>
          </div>

          {/* Address Section - Luxury Design */}
          <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 p-6">
            <h2 className="text-lg font-serif font-bold text-brand-dark dark:text-brand-light mb-4 flex items-center gap-2 tracking-wide">
              <MapPin size={20} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
              Endereço
            </h2>
            
            <div className="space-y-4">
              {/* CEP */}
              <div>
                <label className="block text-sm font-medium text-brand-dark dark:text-brand-light mb-2 uppercase tracking-wider">
                  CEP
                </label>
                <div className="relative">
                  <MapPin size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                  <input
                    type="text"
                    value={formData.address_cep}
                    onChange={(e) => handleCepChange(e.target.value)}
                    placeholder="12345-678"
                    maxLength={9}
                    className={`w-full pl-10 pr-4 py-3 bg-transparent border text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 tracking-wide ${
                      validationErrors.address_cep
                        ? 'border-red-600/30 dark:border-red-400/30 focus:ring-red-600 dark:focus:ring-red-400'
                        : 'border-brand-dark/20 dark:border-brand-light/20 focus:ring-brand-dark dark:focus:ring-brand-light'
                    }`}
                  />
                  {isLookingUpCep && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-brand-dark dark:border-brand-light border-t-transparent animate-spin"></div>
                    </div>
                  )}
                </div>
                {validationErrors.address_cep && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1 tracking-wide">{validationErrors.address_cep}</p>
                )}
              </div>

              {/* Street */}
              <div>
                <label className="block text-sm font-medium text-brand-dark dark:text-brand-light mb-2 uppercase tracking-wider">
                  Logradouro
                </label>
                <input
                  type="text"
                  value={formData.address_street}
                  onChange={(e) => handleChange('address_street', e.target.value)}
                  placeholder="Rua, Avenida, etc."
                  className="w-full py-3 px-4 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                />
              </div>

              {/* Number and Complement */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-brand-dark dark:text-brand-light mb-2 uppercase tracking-wider">
                    Número
                  </label>
                  <input
                    type="text"
                    value={formData.address_number}
                    onChange={(e) => handleChange('address_number', e.target.value)}
                    placeholder="123"
                    className="w-full py-3 px-4 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-dark dark:text-brand-light mb-2 uppercase tracking-wider">
                    Complemento
                  </label>
                  <input
                    type="text"
                    value={formData.address_complement}
                    onChange={(e) => handleChange('address_complement', e.target.value)}
                    placeholder="Apto, Bloco, etc."
                    className="w-full py-3 px-4 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                  />
                </div>
              </div>

              {/* Neighborhood */}
              <div>
                <label className="block text-sm font-medium text-brand-dark dark:text-brand-light mb-2 uppercase tracking-wider">
                  Bairro
                </label>
                <input
                  type="text"
                  value={formData.address_neighborhood}
                  onChange={(e) => handleChange('address_neighborhood', e.target.value)}
                  placeholder="Nome do bairro"
                  className="w-full py-3 px-4 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                />
              </div>

              {/* City and State */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-brand-dark dark:text-brand-light mb-2 uppercase tracking-wider">
                    Cidade
                  </label>
                  <input
                    type="text"
                    value={formData.address_city}
                    onChange={(e) => handleChange('address_city', e.target.value)}
                    placeholder="Nome da cidade"
                    className="w-full py-3 px-4 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-dark dark:text-brand-light mb-2 uppercase tracking-wider">
                    Estado
                  </label>
                  <select
                    value={formData.address_state}
                    onChange={(e) => handleChange('address_state', e.target.value)}
                    className={`w-full py-3 px-4 bg-transparent border text-brand-dark dark:text-brand-light focus:ring-2 tracking-wide ${
                      validationErrors.address_state
                        ? 'border-red-600/30 dark:border-red-400/30 focus:ring-red-600 dark:focus:ring-red-400'
                        : 'border-brand-dark/20 dark:border-brand-light/20 focus:ring-brand-dark dark:focus:ring-brand-light'
                    }`}
                  >
                    <option value="">Selecione</option>
                    {BRAZILIAN_STATES.map(state => (
                      <option key={state.code} value={state.code}>
                        {state.code} - {state.name}
                      </option>
                    ))}
                  </select>
                  {validationErrors.address_state && (
                    <p className="text-red-600 dark:text-red-400 text-sm mt-1 tracking-wide">{validationErrors.address_state}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Labels Section - Luxury Design */}
          <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 p-6">
            <h2 className="text-lg font-serif font-bold text-brand-dark dark:text-brand-light mb-4 flex items-center gap-2 tracking-wide">
              <Tag size={20} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
              Preferências
            </h2>

            <div className="space-y-4">
              {/* Current Labels */}
              <div>
                <label className="block text-sm font-medium text-brand-dark dark:text-brand-light mb-3 uppercase tracking-wider">
                  Preferências do Cliente
                </label>
                {selectedLabels.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {selectedLabels.map((label) => (
                      <LabelChip
                        key={label.id}
                        label={label}
                        size="medium"
                        showRemove={true}
                        onRemove={handleLabelRemove}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-brand-dark/60 dark:text-brand-light/60 text-sm mb-3 tracking-wide">Nenhuma preferência selecionada</p>
                )}

                {/* Add Label Button */}
                <button
                  type="button"
                  onClick={() => setIsLabelSelectorOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 text-brand-dark dark:text-brand-light font-medium uppercase tracking-widest text-sm hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300"
                >
                  <Plus size={16} strokeWidth={1.5} />
                  Adicionar Preferências
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Label Selector Modal */}
      <LabelSelector
        isOpen={isLabelSelectorOpen}
        onClose={() => setIsLabelSelectorOpen(false)}
        onSelectionChange={handleLabelSelectionChange}
        selectedLabels={selectedLabels}
        title="Selecionar Preferências do Cliente"
        allowMultiple={true}
      />
    </div>
  )
}

export default ClientFormPage